import { z } from 'zod';
import { ATTENDEE_RESPONSE_STATUSES, CALENDAR_PROVIDERS, RESERVATION_STATUSES, RESERVATION_VISIBILITIES, } from '../constants/index.js';
import { idSchema } from './common.js';
export const reservationAttendeeSchema = z.object({
    id: idSchema,
    userId: idSchema.nullable(),
    email: z.string().email(),
    displayName: z.string().nullable(),
    /**
     * Profile picture of the attendee, when they have an account here. A guest
     * invited by email alone has none, and the interface falls back to initials.
     */
    avatarUrl: z.string().nullable().default(null),
    responseStatus: z.enum(ATTENDEE_RESPONSE_STATUSES),
});
export const reservationSchema = z.object({
    id: idSchema,
    roomId: idSchema,
    organizerUserId: idSchema.nullable(),
    organizer: z
        .object({
        id: idSchema.nullable(),
        displayName: z.string(),
        email: z.string().nullable(),
        avatarUrl: z.string().nullable(),
    })
        .nullable(),
    title: z.string(),
    description: z.string().nullable(),
    startAtUtc: z.string(),
    endAtUtc: z.string(),
    attendeeCount: z.number().int(),
    visibility: z.enum(RESERVATION_VISIBILITIES),
    status: z.enum(RESERVATION_STATUSES),
    calendarProvider: z.enum(CALENDAR_PROVIDERS),
    externalEvent: z.boolean(),
    /**
     * Teams join link. Present only for rooms backed by a Microsoft 365 resource
     * mailbox; a room in local mode hosts no meeting. Redacted reservations never
     * carry it, since the link is a way in to someone else's meeting.
     */
    onlineMeetingUrl: z.string().url().nullable().default(null),
    /** True when the caller may not see details; UI must render only "Ocupada". */
    redacted: z.boolean().default(false),
    notes: z.string().nullable().optional(),
    cancelReason: z.string().nullable(),
    cancelledAt: z.string().nullable(),
    version: z.number().int(),
    attendees: z.array(reservationAttendeeSchema).default([]),
    canEdit: z.boolean().default(false),
    canCancel: z.boolean().default(false),
    createdAt: z.string(),
    updatedAt: z.string(),
});
export const createReservationSchema = z
    .object({
    roomId: idSchema,
    title: z.string().trim().min(3).max(180),
    description: z.string().trim().max(2000).nullish(),
    startAtUtc: z.string().datetime(),
    endAtUtc: z.string().datetime(),
    attendeeCount: z.number().int().min(1).max(1000),
    attendeeEmails: z.array(z.string().email()).max(100).default([]),
    visibility: z.enum(RESERVATION_VISIBILITIES).default('normal'),
    notes: z.string().trim().max(1000).nullish(),
})
    .refine((v) => new Date(v.endAtUtc) > new Date(v.startAtUtc), {
    message: 'La hora final debe ser mayor que la inicial',
    path: ['endAtUtc'],
});
export const updateReservationSchema = z.object({
    roomId: idSchema.optional(),
    title: z.string().trim().min(3).max(180).optional(),
    description: z.string().trim().max(2000).nullish(),
    startAtUtc: z.string().datetime().optional(),
    endAtUtc: z.string().datetime().optional(),
    attendeeCount: z.number().int().min(1).max(1000).optional(),
    attendeeEmails: z.array(z.string().email()).max(100).optional(),
    visibility: z.enum(RESERVATION_VISIBILITIES).optional(),
    notes: z.string().trim().max(1000).nullish(),
    /** Optimistic concurrency guard; mismatched value returns VERSION_CONFLICT. */
    version: z.number().int().min(0),
});
export const cancelReservationSchema = z.object({
    reason: z.string().trim().max(500).optional(),
});
export const myReservationsQuerySchema = z.object({
    scope: z.enum(['upcoming', 'past', 'cancelled', 'pending', 'all']).default('upcoming'),
    search: z.string().trim().max(120).optional(),
    roomId: idSchema.optional(),
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(25),
});
export const calendarQuerySchema = z.object({
    start: z.string().datetime(),
    end: z.string().datetime(),
    roomIds: z
        .union([z.array(idSchema), idSchema])
        .transform((v) => (Array.isArray(v) ? v : [v]))
        .optional(),
    search: z.string().trim().max(120).optional(),
});
export const availabilityQuerySchema = z.object({
    start: z.string().datetime(),
    end: z.string().datetime(),
    capacity: z.coerce.number().int().min(1).optional(),
    featureIds: z
        .union([z.array(idSchema), idSchema])
        .transform((v) => (Array.isArray(v) ? v : [v]))
        .optional(),
    roomIds: z
        .union([z.array(idSchema), idSchema])
        .transform((v) => (Array.isArray(v) ? v : [v]))
        .optional(),
    excludeReservationId: idSchema.optional(),
});
export const availabilitySlotSchema = z.object({
    roomId: idSchema,
    startAtUtc: z.string(),
    endAtUtc: z.string(),
    free: z.boolean(),
    reason: z.string().nullable(),
});
export const conflictSuggestionSchema = z.object({
    type: z.enum(['alternative_time', 'alternative_room']),
    roomId: idSchema,
    roomName: z.string(),
    startAtUtc: z.string(),
    endAtUtc: z.string(),
});
//# sourceMappingURL=reservation.js.map